import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { ColumnDef } from '@tanstack/react-table';
import { flexRender, type SortingState } from '@tanstack/react-table';

import { usePhrReactTable } from '../lib/usePhrReactTable';
import { ArrowUpDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/shadcn/table';

import type { TableConfig, TableRowData } from '@/shared/lib/table';
import { getTableColumnKeys, getTableRowId, toTableSearchText } from '@/shared/lib/table';

type PhrColumnLayout = NonNullable<TableConfig['columnLayout']>[string];

function renderCellValue(value: unknown): ReactNode {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function renderBadgeValue(value: unknown): ReactNode {
  if (value === null || value === undefined) return '—';
  const label = typeof value === 'string' ? value : String(value);
  if (!label) return '—';
  return (
    <Badge variant="secondary" className="rounded-full">
      {label}
    </Badge>
  );
}

function getCellBadgeToneClass(tone: 'green' | 'red' | 'neutral'): string {
  switch (tone) {
    case 'green':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    case 'red':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    case 'neutral':
      return 'bg-muted text-foreground';
    default:
      return 'bg-muted text-foreground';
  }
}

interface PhrTableProps {
  rows: object[];
  config?: TableConfig;
  highlightedRowId?: string;
  highlightedText?: string;
  onHighlightConsumed?: () => void;
}

export function PhrTable({
  rows,
  config,
  highlightedRowId,
  highlightedText,
  onHighlightConsumed,
}: PhrTableProps) {
  const data = useMemo(() => rows, [rows]);
  const dataKeys = useMemo(() => new Set(getTableColumnKeys(rows)), [rows]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [sorting, setSorting] = useState<SortingState>(
    config?.defaultSort && dataKeys.has(config.defaultSort.id) ? [config.defaultSort] : []
  );

  const columns = useMemo<ColumnDef<object>[]>(() => {
    const keys = Array.from(dataKeys);
    const hidden = new Set(config?.hiddenColumns ?? []);
    const sortable = config?.sortableColumns ? new Set(config.sortableColumns) : undefined;
    const badgeColumns = new Set(config?.badgeColumns ?? []);
    const cellBadgeByColumn = config?.cellBadgeByColumn;
    const defaultCellBadgeMapper = config?.defaultCellBadgeMapper;

    const extraKeys = keys.filter((k) => !config?.columnOrder?.includes(k));
    if (config?.defaultSort?.desc) extraKeys.reverse();
    const ordered = config?.columnOrder ? [...config.columnOrder, ...extraKeys] : keys;

    const headerMap = config?.headerByColumn;

    return ordered
      .filter((key) => !hidden.has(key))
      .map((key) => ({
        accessorKey: key,
        header: ({ column }) => {
          const label = headerMap?.[key] ?? key;
          if (!label) return null;
          if (!column.getCanSort()) {
            return label;
          }
          return (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="-ml-2 h-8 px-2 font-medium"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            >
              {label}
              <ArrowUpDown className="ml-2 h-4 w-4" aria-hidden="true" />
            </Button>
          );
        },
        cell: ({ row }) => {
          const record = row.original as TableRowData;
          const value = record[key];
          const cellBadgeMapper = cellBadgeByColumn?.[key] ?? defaultCellBadgeMapper;
          if (cellBadgeMapper !== undefined) {
            const cellBadge = cellBadgeMapper({ value, row: record, columnKey: key });
            if (cellBadge !== null) {
              const label = cellBadge.label ?? renderCellValue(value);
              return (
                <Badge
                  variant="secondary"
                  className={cn('rounded-full', getCellBadgeToneClass(cellBadge.tone))}
                >
                  {label}
                </Badge>
              );
            }
          }
          if (badgeColumns.has(key)) {
            return renderBadgeValue(value);
          }
          return renderCellValue(value);
        },
        enableSorting: sortable ? sortable.has(key) : true,
        meta: config?.columnLayout?.[key] satisfies PhrColumnLayout | undefined,
      }));
  }, [config, dataKeys]);

  const table = usePhrReactTable({
    data,
    columns,
    getRowId: (row, index) => getTableRowId(row as TableRowData, index),
    onSortingChange: setSorting,
    state: { sorting },
  });
  const visibleRows = table.getRowModel().rows;

  useEffect(() => {
    const target = highlightedRowId?.trim();
    const normalizedText = highlightedText?.trim().toLowerCase();
    const isTextProvided = normalizedText !== undefined && normalizedText !== '';
    if (target === undefined && !isTextProvided) {
      return;
    }

    const rowById = target ? visibleRows.find((row) => row.id === target) : undefined;
    const rowByText =
      rowById !== undefined || !isTextProvided
        ? undefined
        : visibleRows.find((row) => {
            const record = row.original as TableRowData;
            const titleCandidates = [record['name'], record['title'], record['description']];
            return titleCandidates.some((candidate) =>
              toTableSearchText(candidate).toLowerCase().includes(normalizedText)
            );
          });
    const matchedRow = rowById ?? rowByText;
    if (matchedRow === undefined) {
      return;
    }

    const selector = `[data-row-id="${matchedRow.id.replace(/"/g, '\\"')}"]`;
    const element = containerRef.current?.querySelector<HTMLTableRowElement>(selector);
    if (element === null || element === undefined) {
      return;
    }

    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const highlightClasses = ['bg-routine-badge/35', 'ring-2', 'ring-routine-badge/70'];
    element.classList.add(...highlightClasses);
    const timer = window.setTimeout(() => {
      element.classList.remove(...highlightClasses);
    }, 1800);

    onHighlightConsumed?.();
    return () => {
      void timer;
    };
  }, [highlightedRowId, highlightedText, onHighlightConsumed, visibleRows]);

  return (
    <div ref={containerRef}>
      <div className="-mr-4 overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const meta = header.column.columnDef.meta as PhrColumnLayout | undefined;
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        meta?.maxWidthClass,
                        meta?.wrap === true && 'whitespace-normal',
                        meta?.wrap !== true && meta?.maxWidthClass !== undefined && 'truncate',
                        meta?.align === 'right' && 'text-right',
                        meta?.sticky && 'sticky left-0 z-10 bg-background'
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-row-id={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta as PhrColumnLayout | undefined;
                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          meta?.maxWidthClass,
                          meta?.wrap === true && 'whitespace-normal',
                          meta?.wrap !== true && meta?.maxWidthClass !== undefined && 'truncate',
                          meta?.align === 'right' && 'text-right',
                          meta?.sticky && 'sticky left-0 z-10 bg-background'
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
